import type { CSSProperties, HTMLAttributes, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

interface ScrollRevealProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  delay?: number;
  distance?: "sm" | "md" | "lg";
  once?: boolean;
}

const revealDistance: Record<
  NonNullable<ScrollRevealProps["distance"]>,
  string
> = {
  lg: "36px",
  md: "24px",
  sm: "16px",
};

function prefersReducedMotion(): boolean {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return false;
  }

  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function ScrollReveal({
  children,
  className,
  delay = 0,
  distance = "md",
  once = true,
  style,
  ...props
}: ScrollRevealProps): JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(() => prefersReducedMotion());

  useEffect(() => {
    if (prefersReducedMotion() || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);

      return;
    }

    const node = ref.current;

    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);

          if (once) {
            observer.disconnect();
          }

          return;
        }

        if (!once) {
          setIsVisible(false);
        }
      },
      {
        rootMargin: "0px 0px -8% 0px",
        threshold: 0.18,
      },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [once]);

  return (
    <div
      className={cn(
        "scroll-reveal",
        isVisible && "scroll-reveal-visible",
        className,
      )}
      ref={ref}
      style={
        {
          "--reveal-delay": `${String(delay)}ms`,
          "--reveal-distance": revealDistance[distance],
          ...style,
        } as CSSProperties
      }
      {...props}
    >
      {children}
    </div>
  );
}
